import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { EmbeddedCategoryLightSchema } from './models.categories';
import { URLParamsSchema } from './models.generic';
import { UserProfileSchema } from './models.users';

extendZodWithOpenApi(z);

export type EmbeddedToolLight = z.infer<typeof EmbeddedToolLightSchema>;

export const EmbeddedToolLightSchema = z
  .object({
    id: z.string().openapi({ description: 'Tool ID', example: 'timely' }),
    title: z.string().openapi({ description: 'Tool title', example: 'Timely/app' }),
    icon_url: z
      .string()
      .nullable()
      .optional()
      .openapi({ description: 'Tool icon URL', example: 'https://...' }),
    raw: z
      .string()
      .nullable()
      .optional()
      .openapi({ description: 'Raw tool data', example: 'Timely' }),
    // category: CategoryEnumSchema.nullable().optional(),
  })
  .strict();

export type Tool = z.infer<typeof ToolSchema>;

export const ToolSchema = EmbeddedToolLightSchema.extend({
  description: z
    .string()
    .nullable()
    .openapi({ description: 'Tool description', example: 'Zoom is a video conferencing service.' }),
  url_homepage: z
    .string()
    .nullable()
    .openapi({ description: 'Tool homepage URL', example: 'https://zoom.us' }),
  url_support: z
    .string()
    .nullable()
    .openapi({ description: 'Tool support URL', example: 'https://support.zoom.us' }),
  aliases: z.array(z.string()).openapi({ description: 'Tool aliases', example: ['Zoom Desktop'] }),
  domains: z
    .array(z.string())
    .nullable()
    .optional()
    .openapi({ description: 'Tool aliases', example: ['zoom.us'] }),
  color: z
    .string()
    .nullable()
    .openapi({ description: 'Tool color', example: 'rgba(123,132,123,0.5)' }),
  category: EmbeddedCategoryLightSchema.nullable().optional(),
  provider: z
    .object({
      account_key: z
        .string()
        .nullable()
        .openapi({ description: 'Account provider key', example: 'zoom-account' }),
      personal_key: z
        .string()
        .nullable()
        .openapi({ description: 'Personal provider key', example: 'zoom-personal' }),
      account_operations: z
        .array(z.string()) // Todo: BaseOperationIds
        .openapi({ description: 'Account operations', example: ['importIdentity'] }),
      personal_operations: z.array(z.string()).openapi({
        description: 'Personal operations',
        example: ['importIdentity', 'importMeetings'],
      }),
    })
    .nullable()
    .openapi({
      description: 'Information about the provider (integration) for this tool',
    }),
  connection_info: z
    .object({
      current_user_can_manage: z.boolean().openapi({
        description: 'Whether the current user can manage the connection',
        example: true,
      }),
      id: z.string().uuid().openapi({
        description: 'Connection ID',
        example: '00000000-0000-0000-0000-000000000000',
      }),
      nango_connection_id: z.string().nullable().openapi({
        description:
          'Nango Connection Id. Nullable ONLY if it fails to delete itself (after Nango connection is deleted)',
        example: 'account_00000000-0000-0000-0000-000000000000',
      }),
      scope: URLParamsSchema.scope,
      created_at: z.string().datetime({ offset: true }).openapi({
        description: 'When the connection was created',
        example: '2021-01-01T00:00:00.000Z',
      }),
      disconnected_at: z
        .string()
        .datetime({ offset: true })
        .nullable()
        .default(null)
        .describe(
          `When the connection was disconnected. Nullable if the connection is still active`
        ),
      last_active_at: z.string().datetime({ offset: true }).openapi({
        description: 'Last time the connection was active',
        example: '2021-01-01T00:00:00.000Z',
      }),
      managing_user: UserProfileSchema.omit({ timely_id: true }).nullable(),
      error_count: z.number().int().openapi({
        description: 'Number of errors from last operations runs',
        example: 0,
      }),
      success_count: z.number().int().openapi({
        description: 'Number of successful operations runs',
        example: 3,
      }),
      account: z.object({
        id: z.string().uuid(),
        team_name: z.string().nullable(),
        personal_account: z.boolean(),
      }),
    })
    .nullable()
    .openapi({
      description: 'Information about the connection to this tool, if any',
    }),
});
